import { useMemo } from 'react';
import { ColorId } from 'referral-types';
import { getColorStyleClassName } from '../constants';

interface GroupColorStylesOptions {
  backgroundColorId?: ColorId;
  borderColorId?: ColorId;
  textColorId?: ColorId;
}

interface GroupColorStyles {
  background: string | undefined;
  backgroundHover: string | undefined;
  border: string | undefined;
  text: string | undefined;
  textHover: string | undefined;
  divider: string | undefined;
  borderBackground: string | undefined;
  textBackground: string | undefined;
}

/**
 * Custom hook to get all group color style classnames at once.
 * Returns an object with all possible style types based on the provided color IDs.
 *
 * @param colorId - Optional single color ID to use for all style types
 * @param options - Optional object to specify different color IDs for background, border, and text
 * @returns Object containing all style type classnames
 */
export function useColorStyles(colorId?: ColorId, options?: GroupColorStylesOptions): GroupColorStyles {
  const backgroundColorId = options?.backgroundColorId ?? colorId;
  const borderColorId = options?.borderColorId ?? colorId;
  const textColorId = options?.textColorId ?? colorId;

  return useMemo(() => {
    return {
      background: getColorStyleClassName({
        colorId: backgroundColorId,
        type: 'background',
      }),
      backgroundHover: getColorStyleClassName({
        colorId: backgroundColorId,
        type: 'backgroundHover',
      }),
      textHover: getColorStyleClassName({
        colorId: textColorId,
        type: 'textHover',
      }),
      border: getColorStyleClassName({
        colorId: borderColorId,
        type: 'border',
      }),
      text: getColorStyleClassName({
        colorId: textColorId,
        type: 'text',
      }),
      divider: getColorStyleClassName({
        colorId: borderColorId,
        type: 'divider',
      }),
      borderBackground: getColorStyleClassName({
        colorId: backgroundColorId,
        type: 'borderBackground',
      }),
      textBackground: getColorStyleClassName({
        colorId: backgroundColorId,
        type: 'textBackground',
      }),
    };
  }, [backgroundColorId, borderColorId, textColorId, colorId]);
}
